import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ASSETS } from "@/lib/api";
import SecurePayments from "@/components/SecurePayments";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { useSocialProfiles } from "@/lib/useSocialProfiles";
import { PlatformIcon } from "@/lib/platformIcons";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const { lang } = useLocale();
  const localised = (p) => localisedTo(p, lang);
  const { music_platforms, social_media, loading: socialLoading } = useSocialProfiles();
  const isAr = (i18n.language || lang || "").startsWith("ar");

  const navLinks = [
    { label: t("nav.home"),           href: "#home" },
    { label: t("nav.about"),          href: "#about" },
    { label: t("nav.music"),          to: "/music" },
    { label: t("nav.arabicRemixes"),  to: "/arabic-remixes", testid: "footer-nav-arabic-remixes" },
    { label: t("nav.media"),          to: "/media" },
    { label: t("nav.contact"),        href: "#contact" },
  ];

  const onMediaClick = () => {
    try { window.__consent?.granted && window.dispatchEvent(new CustomEvent("analytics", { detail: { event: "footer_media_click" } })); } catch (_) {}
  };

  return (
    <footer data-testid="site-footer" className="relative bg-black border-t border-white/5">
      <div className="border-y border-white/10 py-6 bg-black/40">
        <Marquee gradient={false} speed={30} direction={lang === "ar" ? "right" : "left"}>
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 pr-10">
              {["DIGITAL MUSIC STORE", "•", "INSTANT DOWNLOADS", "•", "DJ SPY OFFICIAL", "•", "PROFESSIONAL DJ BOOKING", "•"].map((str, i) => (
                <span
                  key={i + "-" + k}
                  className={`font-anton text-4xl md:text-5xl tracking-widest ${
                    str === "•" ? "text-red-brand" : "stroke-text"
                  }`}
                  dir="ltr"
                >
                  {str}
                </span>
              ))}
            </div>
          ))}
        </Marquee>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-6">
          <span className="relative inline-flex items-start">
            <img src={ASSETS.logo} alt="DJ SPY Official logo" loading="lazy" className="h-14 w-auto" />
            <sup
              aria-hidden="true"
              data-testid="footer-logo-tm"
              className="text-white/70 text-[11px] font-mono leading-none ml-0.5 mt-0.5 select-none"
            >
              ®
            </sup>
          </span>
          <p className="text-white/55 mt-4 max-w-md">
            {t("footer.brandBlurb")}
          </p>
          <div className="mt-6">
            <LanguageSwitcher variant="footer" />
          </div>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="font-mono text-xs md:text-sm tracking-[0.3em] text-white/80 uppercase">{t("footer.navigate")}</div>
          <div className="mt-4 space-y-2 text-white/70">
            {navLinks.map((l) => (
              l.to ? (
                <Link
                  key={l.to}
                  to={localised(l.to)}
                  data-testid={`footer-${l.to.replace(/\//g, "")}`}
                  onClick={onMediaClick}
                  className="block hover:text-red-brand transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`footer-${l.href.replace("#", "")}`}
                  className="block hover:text-red-brand transition-colors"
                >
                  {l.label}
                </a>
              )
            ))}
          </div>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="space-y-6">
            <div>
              <div className="font-mono text-xs md:text-sm tracking-[0.3em] text-white/80 uppercase" data-testid="footer-music-label">
                {isAr ? "منصات الموسيقى" : "MUSIC PLATFORMS"}
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="footer-music-list">
                {(!socialLoading) && music_platforms.map((l) => (
                  <a
                    key={l.platform_slug}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`DJ SPY on ${l.display_name}`}
                    data-testid={`footer-music-${l.platform_slug}`}
                    className="inline-flex items-center justify-center h-9 w-9 rounded border border-white/10 text-white/70 hover:text-red-brand hover:border-red-brand/40 focus:text-red-brand focus:outline-none focus:ring-2 focus:ring-red-brand/50 transition-colors"
                    title={l.display_name}
                  >
                    <PlatformIcon slug={l.icon_hint || l.platform_slug} size={16} />
                  </a>
                ))}
              </div>
            </div>
            <div>
              <div className="font-mono text-xs md:text-sm tracking-[0.3em] text-white/80 uppercase" data-testid="footer-social-label">
                {isAr ? "وسائل التواصل الاجتماعي" : t("footer.social")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="footer-social-list">
                {(!socialLoading) && social_media.map((l) => (
                  <a
                    key={l.platform_slug}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`DJ SPY on ${l.display_name}`}
                    data-testid={`footer-social-${l.platform_slug}`}
                    className="inline-flex items-center justify-center h-9 w-9 rounded border border-white/10 text-white/70 hover:text-red-brand hover:border-red-brand/40 focus:text-red-brand focus:outline-none focus:ring-2 focus:ring-red-brand/50 transition-colors"
                    title={l.display_name}
                  >
                    <PlatformIcon slug={l.icon_hint || l.platform_slug} size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-2">
          <div className="font-mono text-xs md:text-sm tracking-[0.3em] text-white/80 uppercase">{t("footer.legal")}</div>
          <div className="mt-4 space-y-2 text-white/70">
            <Link to={localised("/privacy")} data-testid="footer-privacy" className="block hover:text-red-brand transition-colors">{t("footer.privacy")}</Link>
            <Link to={localised("/terms")} data-testid="footer-terms" className="block hover:text-red-brand transition-colors">{t("footer.terms")}</Link>
            <Link to={localised("/refund")} data-testid="footer-refund" className="block hover:text-red-brand transition-colors">{t("footer.refund")}</Link>
            <button
              type="button"
              data-testid="footer-cookies"
              onClick={() => window.dispatchEvent(new Event("djspy-open-consent"))}
              className="block hover:text-red-brand transition-colors text-start"
            >
              {t("footer.cookies")}
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 py-6">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col gap-6 md:gap-4">
          <SecurePayments variant="full" />
          <div className="flex flex-col md:flex-row justify-between items-center text-white/40 text-xs font-mono">
            <div>{t("footer.rightsReserved", { year: new Date().getFullYear() })}</div>
            <div>{t("footer.madeFor")}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
